import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, Button, Alert, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tab, Tabs,
  CircularProgress, Tooltip, Divider, MenuItem, LinearProgress,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Close, Edit, Visibility, School, Save, ContentCopy, OpenInNew,
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Fields a super admin may edit on an inquiry (mirrors the backend PATCH /:id).
const EDIT_BLANK = {
  contactName: '', contactEmail: '', contactPhone: '',
  schoolName: '', address: '', city: '', county: '', postalCode: '', website: '',
  numberOfStudents: '', numberOfBoardingStudents: '',
  numberOfTeachers: '', numberOfSupportStaff: '', numberOfAdminStaff: '',
  currentSystem: '', systemDetails: '', additionalInfo: '', adminNotes: '',
  inquiryType: 'full',
};

export default function AdminSchoolInquiries() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);      // first paint only
  const [refreshing, setRefreshing] = useState(false); // background reloads
  const [tab, setTab] = useState(0); // 0=pending, 1=approved, 2=completed, 3=rejected
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [viewDlg, setViewDlg] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EDIT_BLANK);
  const [createDlg, setCreateDlg] = useState(false);
  const [rejectDlg, setRejectDlg] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [createdResult, setCreatedResult] = useState(null); // success info after create-school
  const [copied, setCopied] = useState(false);

  const [schoolForm, setSchoolForm] = useState({
    schoolName: '',
    registrationNo: '',
    kraPin: '',
    address: '',
    city: '',
    county: '',
    postalCode: '',
    phone: '',
    email: '',
    website: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    databaseName: '',
  });
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: createDlg, value: schoolForm, onClose: () => setCreateDlg(false) });

  // Reloads the list without blanking the page. The full-screen loader is driven
  // only by the initial `loading` state (true until the first fetch resolves);
  // later reloads after actions show a thin top bar instead.
  const loadInquiries = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/api/school-inquiries`, { headers: hdrs() });
      setInquiries(data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load inquiries');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const handleViewClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setEditMode(false);
    setError('');
    setViewDlg(true);
  };

  const beginEdit = () => {
    const i = selectedInquiry;
    setEditForm({
      ...EDIT_BLANK,
      ...Object.fromEntries(
        Object.keys(EDIT_BLANK).map(k => [k, i[k] == null ? '' : i[k]])
      ),
      inquiryType: i.inquiryType || 'full',
    });
    setError('');
    setEditMode(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.patch(
        `${API}/api/school-inquiries/${selectedInquiry._id}`,
        editForm,
        { headers: hdrs() }
      );
      setSelectedInquiry(data);
      setEditMode(false);
      loadInquiries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const setF = (name) => (e) => setEditForm(f => ({ ...f, [name]: e.target.value }));

  const copyPassword = async () => {
    if (!createdResult?.temporaryPassword) return;
    try {
      await navigator.clipboard.writeText(createdResult.temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the admin can still read/select it */
    }
  };

  const handleCreateClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setSchoolForm({
      schoolName: inquiry.schoolName || '',
      registrationNo: '',
      kraPin: '',
      address: inquiry.address || '',
      city: inquiry.city || '',
      county: inquiry.county || '',
      postalCode: inquiry.postalCode || '',
      phone: inquiry.contactPhone || '',
      email: '',
      website: inquiry.website || '',
      adminEmail: inquiry.contactEmail || '',
      adminFirstName: inquiry.contactName.split(' ')[0] || '',
      adminLastName: inquiry.contactName.split(' ').slice(1).join(' ') || '',
      databaseName: `db_${inquiry.schoolName.toLowerCase().replace(/\s+/g, '_')}`,
    });
    setCreateDlg(true);
  };

  const handleApproveClick = async (inquiry) => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/api/school-inquiries/${inquiry._id}/approve`, {}, { headers: hdrs() });
      setError('');
      loadInquiries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    } finally {
      setSaving(false);
    }
  };

  const handleRejectClick = (inquiry) => {
    setSelectedInquiry(inquiry);
    setRejectDlg(true);
    setRejectionReason('');
  };

  const submitReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.put(
        `${API}/api/school-inquiries/${selectedInquiry._id}/reject`,
        { rejectionReason: rejectionReason.trim() },
        { headers: hdrs() }
      );
      setRejectDlg(false);
      loadInquiries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject');
    } finally {
      setSaving(false);
    }
  };

  const submitCreateSchool = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/api/school-inquiries/${selectedInquiry._id}/create-school`,
        schoolForm,
        { headers: hdrs() }
      );
      setCreateDlg(false);
      setCreatedResult({
        schoolName: data.school?.name || schoolForm.schoolName,
        temporaryPassword: data.temporaryPassword || null,
      });
      loadInquiries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create school');
    } finally {
      setSaving(false);
    }
  };

  const statuses = {
    pending: inquiries.filter(i => i.status === 'pending'),
    approved: inquiries.filter(i => i.status === 'approved'),
    completed: inquiries.filter(i => i.status === 'completed'),
    rejected: inquiries.filter(i => i.status === 'rejected'),
  };

  const statusList = [statuses.pending, statuses.approved, statuses.completed, statuses.rejected];
  const currentInquiries = statusList[tab];

  const statusColor = {
    pending: 'warning',
    approved: 'info',
    completed: 'success',
    rejected: 'error',
  };

  if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBack />
          </IconButton>
          <School sx={{ fontSize: 32, color: '#1976d2' }} />
          <Typography variant="h5" fontWeight={700}>
            School Inquiries Management
          </Typography>
        </Box>

        {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label={`Pending (${statuses.pending.length})`} />
            <Tab label={`Approved (${statuses.approved.length})`} />
            <Tab label={`Completed (${statuses.completed.length})`} />
            <Tab label={`Rejected (${statuses.rejected.length})`} />
          </Tabs>
        </Card>

        {/* Results Table */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>School Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentInquiries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" py={3}>
                        <Typography color="text.secondary">No {['pending', 'approved', 'completed', 'rejected'][tab]} inquiries</Typography>
                      </TableCell>
                    </TableRow>
                  ) : currentInquiries.map(inquiry => (
                    <TableRow key={inquiry._id} hover>
                      <TableCell fontWeight={600}>{inquiry.schoolName || '(Demo Request)'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{inquiry.contactName}</Typography>
                        <Typography variant="caption" color="text.secondary">{inquiry.contactEmail}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inquiry.inquiryType === 'demo' ? 'Demo' : 'Full'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inquiry.status}
                          size="small"
                          color={statusColor[inquiry.status]}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewClick(inquiry)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {inquiry.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => handleApproveClick(inquiry)}
                                disabled={saving}
                              >
                                <CheckCircle fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                onClick={() => handleRejectClick(inquiry)}
                                color="error"
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {inquiry.status === 'approved' && (
                          <Tooltip title="Create School">
                            <IconButton
                              size="small"
                              onClick={() => handleCreateClick(inquiry)}
                              color="primary"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* View / Edit Details Dialog — the super admin reviews and can edit all fields */}
      <Dialog open={viewDlg} onClose={() => setViewDlg(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <School color="primary" fontSize="small" />
          <Box flex={1}>{editMode ? 'Edit Inquiry Details' : 'Inquiry Details'}</Box>
          {selectedInquiry && (
            <Chip size="small" label={selectedInquiry.status} color={statusColor[selectedInquiry.status]} />
          )}
          {selectedInquiry && !editMode && (
            <Tooltip title="Edit details">
              <IconButton size="small" onClick={beginEdit}><Edit fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {error && editMode && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {selectedInquiry && editMode ? (
            <Grid container spacing={2} mt={0}>
              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">CONTACT</Typography></Divider></Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Contact Name" value={editForm.contactName} onChange={setF('contactName')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Contact Email" value={editForm.contactEmail} onChange={setF('contactEmail')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Contact Phone" value={editForm.contactPhone} onChange={setF('contactPhone')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Type" value={editForm.inquiryType} onChange={setF('inquiryType')}>
                  <MenuItem value="full">Full setup</MenuItem>
                  <MenuItem value="demo">Demo</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">SCHOOL &amp; LOCATION</Typography></Divider></Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth size="small" label="School Name" value={editForm.schoolName} onChange={setF('schoolName')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Website" value={editForm.website} onChange={setF('website')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Address" value={editForm.address} onChange={setF('address')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="City / Town" value={editForm.city} onChange={setF('city')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="County" value={editForm.county} onChange={setF('county')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Postal Code" value={editForm.postalCode} onChange={setF('postalCode')} />
              </Grid>

              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">OPERATIONS</Typography></Divider></Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Students" value={editForm.numberOfStudents} onChange={setF('numberOfStudents')} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Boarding" value={editForm.numberOfBoardingStudents} onChange={setF('numberOfBoardingStudents')} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Teachers" value={editForm.numberOfTeachers} onChange={setF('numberOfTeachers')} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Support Staff" value={editForm.numberOfSupportStaff} onChange={setF('numberOfSupportStaff')} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label="Admin Staff" value={editForm.numberOfAdminStaff} onChange={setF('numberOfAdminStaff')} />
              </Grid>

              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">NOTES</Typography></Divider></Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Current System" value={editForm.currentSystem} onChange={setF('currentSystem')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="System Details" value={editForm.systemDetails} onChange={setF('systemDetails')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Additional Info" value={editForm.additionalInfo} onChange={setF('additionalInfo')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline rows={2} label="Admin Notes (internal)" value={editForm.adminNotes} onChange={setF('adminNotes')} />
              </Grid>
            </Grid>
          ) : selectedInquiry && (() => {
            const i = selectedInquiry;
            const Row = ({ label, value, xs = 6 }) => (value === 0 || value) ? (
              <Grid item xs={xs}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
              </Grid>
            ) : null;
            const dt = (d) => d ? `${new Date(d).toLocaleDateString()} ${new Date(d).toLocaleTimeString()}` : '—';
            return (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">School Name</Typography>
                  <Typography variant="body2" fontWeight={600}>{i.schoolName || '(Demo Request)'}</Typography>
                </Grid>
                <Row label="Type" value={i.inquiryType === 'demo' ? 'Demo' : 'Full setup'} />
                <Row label="Website" value={i.website} />

                <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">CONTACT</Typography></Divider></Grid>
                <Row label="Contact Name" value={i.contactName} />
                <Row label="Contact Email" value={i.contactEmail} />
                <Row label="Contact Phone" value={i.contactPhone} />
                <Row label="Submitted by account" value={i.ownerId ? 'Registered user ✓' : 'No linked account'} />

                {i.inquiryType === 'full' && (
                  <>
                    <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">LOCATION</Typography></Divider></Grid>
                    <Row label="Address" value={i.address} xs={12} />
                    <Row label="City / Town" value={i.city} xs={4} />
                    <Row label="County" value={i.county} xs={4} />
                    <Row label="Postal Code" value={i.postalCode} xs={4} />

                    <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">OPERATIONS</Typography></Divider></Grid>
                    <Row label="Students" value={i.numberOfStudents} xs={4} />
                    <Row label="Boarding" value={i.numberOfBoardingStudents} xs={4} />
                    <Row label="Teachers" value={i.numberOfTeachers} xs={4} />
                    <Row label="Support Staff" value={i.numberOfSupportStaff} xs={4} />
                    <Row label="Admin Staff" value={i.numberOfAdminStaff} xs={4} />

                    {(i.currentSystem || i.systemDetails || i.additionalInfo) && (
                      <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">NOTES</Typography></Divider></Grid>
                    )}
                    <Row label="Current System" value={i.currentSystem} xs={12} />
                    <Row label="System Details" value={i.systemDetails} xs={12} />
                    <Row label="Additional Info" value={i.additionalInfo} xs={12} />
                  </>
                )}

                <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">STATUS</Typography></Divider></Grid>
                <Row label="Submitted" value={dt(i.createdAt)} />
                <Row label="Reviewed" value={i.reviewedAt ? `${dt(i.reviewedAt)} by ${i.reviewedBy || '—'}` : null} />
                <Row label="Rejection Reason" value={i.rejectionReason} xs={12} />
                <Row label="Admin Notes" value={i.adminNotes} xs={12} />
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          {editMode ? (
            <>
              <Button onClick={() => { setEditMode(false); setError(''); }} disabled={saving}>Cancel</Button>
              <Button variant="contained" startIcon={<Save />} onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              {selectedInquiry?.status === 'pending' && (
                <>
                  <Button color="error" onClick={() => { setViewDlg(false); handleRejectClick(selectedInquiry); }}>
                    Reject
                  </Button>
                  <Button variant="contained" color="success" disabled={saving}
                    onClick={() => { setViewDlg(false); handleApproveClick(selectedInquiry); }}>
                    Approve
                  </Button>
                </>
              )}
              {selectedInquiry?.status === 'approved' && (
                <Button variant="contained" onClick={() => { setViewDlg(false); handleCreateClick(selectedInquiry); }}>
                  Create School
                </Button>
              )}
              <Button onClick={() => setViewDlg(false)}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Create School Dialog */}
      {discardDialog}
      <Dialog open={createDlg} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create School from Inquiry</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="School Name *"
                value={schoolForm.schoolName}
                onChange={e => setSchoolForm(f => ({ ...f, schoolName: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration No"
                value={schoolForm.registrationNo}
                onChange={e => setSchoolForm(f => ({ ...f, registrationNo: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Revenue Authority No."
                value={schoolForm.kraPin}
                onChange={e => setSchoolForm(f => ({ ...f, kraPin: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={schoolForm.address}
                onChange={e => setSchoolForm(f => ({ ...f, address: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={schoolForm.city}
                onChange={e => setSchoolForm(f => ({ ...f, city: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="County"
                value={schoolForm.county}
                onChange={e => setSchoolForm(f => ({ ...f, county: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={schoolForm.postalCode}
                onChange={e => setSchoolForm(f => ({ ...f, postalCode: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={schoolForm.phone}
                onChange={e => setSchoolForm(f => ({ ...f, phone: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={schoolForm.email}
                onChange={e => setSchoolForm(f => ({ ...f, email: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Email *"
                value={schoolForm.adminEmail}
                onChange={e => setSchoolForm(f => ({ ...f, adminEmail: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Admin First Name *"
                value={schoolForm.adminFirstName}
                onChange={e => setSchoolForm(f => ({ ...f, adminFirstName: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Admin Last Name *"
                value={schoolForm.adminLastName}
                onChange={e => setSchoolForm(f => ({ ...f, adminLastName: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Database Name *"
                value={schoolForm.databaseName}
                onChange={e => setSchoolForm(f => ({ ...f, databaseName: e.target.value }))}
                size="small"
                helperText="Will be created for this school"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitCreateSchool}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create School'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDlg} onClose={() => setRejectDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Inquiry</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder="Explain why this inquiry is being rejected..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDlg(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitReject}
            disabled={saving}
          >
            {saving ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* School Created — success summary (replaces the old alert) */}
      <Dialog open={!!createdResult} onClose={() => setCreatedResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          School Created
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            School <strong>{createdResult?.schoolName}</strong> was created and linked to the user.
          </Typography>

          {createdResult?.temporaryPassword ? (
            <Box sx={{ bgcolor: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 2, p: 2, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                New admin account — temporary password
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 20, letterSpacing: 1 }}>
                  {createdResult.temporaryPassword}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                  <IconButton size="small" onClick={copyPassword} color={copied ? 'success' : 'default'}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Share this with the admin — they'll be prompted to change it on first login.
              </Typography>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              The user will sign in with their existing account.
            </Alert>
          )}

          <Alert severity="warning" icon={false}>
            <strong>Next step:</strong> provision its database from the Schools admin page to set it Active.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedResult(null)}>Done</Button>
          <Button variant="contained" startIcon={<OpenInNew />}
            onClick={() => { setCreatedResult(null); navigate('/admin/schools'); }}>
            Go to Schools
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
